import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box,
    Typography, 
    Button, 
    Checkbox,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,     
} from '@mui/material';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import useAuthCheck from 'hooks/useAuthCheck';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from 'state/axios';
import { fetchLegMembersByFlt } from 'state/api';
import { getFlagUrl, addTimeToDate, formatTZDate } from 'state/helpers';
import FlexBetween from 'components/FlexBetween'; 
import CrewListDialog from 'components/CrewListDialog';
import InvoiceDialog from './InvoiceDialog';
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const LegMembersDialog = ({ 
    open, 
    onClose, 
    crewCode, 
    crewName, 
    crewLegs,
}) => {
    useAuthCheck();
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const crewRanks = useSelector((state) => state.crewRanks);

    const theme = useTheme();  
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { startDate, endDate } = useDateRangeContext();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [openCrewListDialog, setOpenCrewListDialog] = useState(false);
    const [flight, setFlight] = useState([]);
    const [crewList, setCrewList] = useState([]);
    const [jumpList, setJumpList] = useState([]);
    
    const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
    const [selectedFlights, setSelectedFlights] = useState([]);
    const [crewMembers, setCrewMembers] = useState([]);
    const [invoicedFlights, setInvoicedFlights] = useState([]);
    const [checkboxesDisabled, setCheckboxesDisabled] = useState(true);
            
    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";   
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
    const IATA_AIRLINE_CODE = paramsMap.get("IATA_AIRLINE_CODE") || "QH";
    const EDIT_INVOICE_OFFSET_HOURS = Number(paramsMap.get("EDIT_INVOICE_OFFSET_HOURS")) || 72;
    
    const cabinCrewRanks = useMemo(
        () => crewRanks
            .filter(({ rankType }) => rankType === 'Cabin Crew')
            .map(({ rankCode }) => rankCode),
        [crewRanks]
    );

    useEffect(() => {
        if (!open) return;

        let isMounted = true; 
        const fetchInvoicedFlights = async () => {
            try {
                setError(null);
                setLoading(true);

                const payload = { startDate, endDate };
                const res = await api.post(
                    '/inflightsales/invoicedflights',
                    payload
                );

                if (!isMounted) return;
                setInvoicedFlights(res.data);
                setCheckboxesDisabled(false);
            } catch (error) {
                if (isMounted) {
                    toast.error(error.response?.data?.message || "Failed to fetch invoiced flights!");                    
                    setError(error);
                }
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        fetchInvoicedFlights();
      
        return () => {
            isMounted = false;
        };
    }, [startDate, endDate, open]);

    const getTime = (f, timeMode, field) => 
        timeMode === 'UTC' ? f[field] : f[`${field}LocalBase`];

    function compareCrewCodes(arr1, arr2) {
        // Extract crewCode values from each array
        const crewCodes1 = arr1.map(crew => crew.crewCode);
        const crewCodes2 = arr2.map(crew => crew.crewCode);
    
        // Sort and check if both arrays contain the same elements
        crewCodes1.sort();
        crewCodes2.sort();
    
        // Check if both sorted arrays are equal
        return crewCodes1.length === crewCodes2.length && crewCodes1.every((code, index) => code === crewCodes2[index]);
    }
  
    const handleCheckboxChange = async (f) => {
        // Check if the flight exceeds the allowed offset hours
        const now = new Date();
        if (now - new Date(addTimeToDate(f.flightDate, f.etd)) > EDIT_INVOICE_OFFSET_HOURS * 60 * 60 * 1000) {
            toast.error(`Editing is not allowed after ${EDIT_INVOICE_OFFSET_HOURS} hours.`);
            // setCrewMembers([]);
            return;
        }
        
        // Fetch leg members for the flight
        const { legMembers } = await fetchLegMembersByFlt(
            f.flightDate, 
            f.flightDesc, 
            f.dep
        );
        const legCrews = legMembers.filter(member => 
            cabinCrewRanks.includes(member.post) && !member.pax
        );
        
        setSelectedFlights((prevSelected) => {
            if (prevSelected.includes(f)) {
                // Remove flight from selectedFlights
                const remaining = prevSelected.filter(flt => flt !== f);

                // Clear crew members if no flights remain selected
                if (remaining.length === 0) {
                    setCrewMembers([]);
                }
                return remaining;
            } else {
                // Ensure all selected flights share the same sectorType
                if (prevSelected.length > 0) {
                    const firstSectorType = prevSelected[0].sectorType;                    
                    if (firstSectorType !== f.sectorType) {
                        toast.error("Domestic & International flights cannot be selected together.");
                        return prevSelected;
                    }
                }
                
                // Set crew members if none exist, otherwise validate crew list consistency
                if (crewMembers.length === 0) {
                    setCrewMembers(legCrews);
                } else {
                    // const matchingCrew = isSubset(legCrews.map(crew => crew.crewCode), crewMembers.map(member => member.crewCode));
                    const matchingCrew = compareCrewCodes(legCrews, crewMembers);
                    if (!matchingCrew) {
                        toast.error("Cannot select flights with different crew lists.");
                        return prevSelected;
                    }
                }
                
                if (prevSelected.some(flt => 
                    flt.flightDate === f.flightDate && 
                    flt.flightDesc === f.flightDesc && 
                    flt.dep === f.dep
                )) {
                    return prevSelected;
                }

                return [...prevSelected, f];
            }
        });
    };

    const handleInvoiceClick = () => {
        if (selectedFlights.length > 0) {
            setOpenInvoiceDialog(true); 
        }
    };
    
    const handleCloseDialog = () => {
        setSelectedFlights([]);  
        setCrewMembers([]);  
        onClose();  
    };

    const handleInvoiceSaved = (flights) => {
        setInvoicedFlights(prev => [...prev, ...flights]);
    };
    
    const handleCloseInvoiceDialog = () => {
        setSelectedFlights([]);  
        setCrewMembers([]);
        setOpenInvoiceDialog(false); 
    };
    
    const handleViewClick = async (f) => {
        try {           
            const {flightDetails, legMembers} = await fetchLegMembersByFlt(
                f.flightDate, 
                f.flightDesc, 
                f.dep
            );
            setFlight(flightDetails);
            
            const cabinMembers = legMembers.filter(lm => 
                cabinCrewRanks.includes(lm.post) && !lm.pax
            );
            setCrewList(cabinMembers);
            setJumpList(null);
            setOpenCrewListDialog(true);
        } catch (error) {
            setError(error);
            setOpenCrewListDialog(false);
        } 
    };

    const handleCloseCrewListDialog = () => {
        setOpenCrewListDialog(false);
        setCrewList([]);
    };
    
    const isCheckboxDisabled = useCallback((f) => (
        checkboxesDisabled || // Disable all checkboxes initially
        !!f.pax || 
        new Date(f.flightDate) > new Date() || 
        invoicedFlights.some(inv => 
          inv.flightDate === f.flightDate &&
          (inv.flightDesc || `${IATA_AIRLINE_CODE}${inv.flightNo}`) === f.flightDesc &&
          inv.dep === f.dep
        )
    ), [
        IATA_AIRLINE_CODE,
        checkboxesDisabled, 
        invoicedFlights, 
    ]);;


    return (
        <Dialog 
            open={open} 
            onClose={handleCloseDialog}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${theme.palette.neutral.dark}`}>
                <FlexBetween>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                        {crewCode} - {crewName}
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>
            </DialogTitle>

            <DialogContent>
                {!crewLegs && (
                    <Typography>No flights available</Typography>
                )} 
                {crewLegs && Object.entries(crewLegs).map(([key, flights]) => (
                    <Box 
                        key={key} 
                        borderBottom={`1px solid ${theme.palette.neutral.dark}`}
                    >
                        {flights.map((f, idx) => {                            
                            return (                        
                                <FlexBetween key={idx} py={0.5} gap={1}>
                                    <Typography width="18px" textAlign="left" color="primary">
                                        {f.flightNo}
                                    </Typography>
                                    <Typography width="40px" textAlign="center">
                                        {timeMode === 'UTC' 
                                            ? formatTZDate(f.fullEtd || f.flightDate, 'UTC', 'ddMMMyy') 
                                            : formatTZDate(f.fullEtdLocalBase || f.flightDateLocalBase, 'UTC', 'ddMMMyy')
                                        }
                                    </Typography>
                                    <Typography width="20px" textAlign="center">
                                        {getTime(f, timeMode, 'etd')}
                                    </Typography>                                
                                    <Typography width="16px" textAlign="center">
                                        {f.dep}
                                    </Typography>
                                    <Typography width="16px" textAlign="center">
                                        {f.arr}
                                    </Typography>
                                    <Typography width="20px" textAlign="center">
                                        {getTime(f, timeMode, 'eta')}
                                    </Typography> 
                                    <Typography width="4px" textAlign="center">
                                        {f.pax}
                                    </Typography>
                                    <Checkbox
                                        color="primary"
                                        checked={selectedFlights.includes(f)}
                                        onChange={() => handleCheckboxChange(f)}                                        
                                        disabled={isCheckboxDisabled(f)}                              
                                        sx={{ p: 0.5 }}
                                    />
                                    
                                    <PersonPinIcon
                                        aria-label="View"
                                        sx={{
                                            color: "primary.dark",
                                            cursor: "pointer",
                                            transition: "transform 0.15s, opacity 0.15s",
                                            "&:hover": { transform: "scale(1.1)", opacity: 0.8 },
                                        }}
                                        onClick={() => {
                                            handleViewClick(f);
                                        }}
                                    />
                                </FlexBetween>
                            )}
                        )}
                    </Box>
                ))}
            </DialogContent>
            <DialogActions>            
                {error && <Typography color="error">Error: {error?.message || ''}</Typography>}    
                <Button 
                    variant="outlined" 
                    disabled={loading || selectedFlights.length === 0 || crewMembers.length === 0} 
                    onClick={handleInvoiceClick}
                >
                    INVOICE
                </Button>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={handleCloseDialog}
                >
                    Close
                </Button>
            </DialogActions>
         
            {openInvoiceDialog &&
                <InvoiceDialog
                    open={openInvoiceDialog}
                    onClose={handleCloseInvoiceDialog}
                    crewCode={crewCode} 
                    crewName={crewName}
                    selectedFlights={selectedFlights}
                    crewMembers={crewMembers}
                    onInvoiceSaved={handleInvoiceSaved}
                />
            }

            {openCrewListDialog &&
                <CrewListDialog 
                    open={openCrewListDialog}
                    onClose={handleCloseCrewListDialog}
                    flight={flight}
                    crewList={crewList}
                    jumpList={jumpList}
                />
            }
        </Dialog>
    );
};

export default LegMembersDialog;
